import { useTable } from "react-table";
import { useMemo, useState, useContext } from "react";
import { deleteCategory } from "../../../services/inventory";
import { RequestLoader } from "../../../hooks/context";
import { defaultDateDisplay, truncateString } from "../../../helpers";
import secondaryComponents from "..";
import { useQueryClient } from "react-query";
import primaryComponents from "../../primaryComponents";

const ProductCategoryTableLogic = ({ categories }) => {
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [isOpenEditDialog, setIsOpenEditDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isDeletingCategory, setIsDeletingCategory] = useState(false);
  const { setRequestLoaderProgress } = useContext(RequestLoader);
  const queryClient = useQueryClient();
  const columns = useMemo(
    () => [
      {
        Header: "Category Name",
        accessor: "name",
        Cell: ({ cell: { value } }) => truncateString(value, 12),
      },
      {
        Header: "Sub Categories",
        accessor: "sub_categories",
        Cell: ({ cell: { value } }) => {
          if (!value || value.length === 0) return "N/A";

          const firstSub = value[0]?.name || "Sub-Category";
          const extraSub = value.length - 1;

          return (
            <div className="d-flex align-items-center" style={{ gap: "5px" }}>
              <span className="">{firstSub}</span>
              {extraSub > 0 && (
                <span
                  style={{
                    background: "#C5C5C5",
                    padding: "3px 8px",
                    borderRadius: "8px",
                  }}
                  className="gray--text"
                >
                  +{extraSub}
                </span>
              )}
            </div>
          );
        },
      },

      {
        Header: "Created on",
        accessor: "created_at",
        Cell: ({ cell: { value } }) => defaultDateDisplay(value),
      },
      {
        Header: "Last Updated on",
        accessor: "updated_at",
        Cell: ({ cell: { value } }) => defaultDateDisplay(value),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ cell: { value } }) => (
          <span
            style={{
              color: "#19AE57",
              backgroundColor: "#E6FFF0",
              borderRadius: "8px",
              padding: "10px 16px",
              height: "28px",
              fontSize: "12px",
            }}
          >
            {value}
          </span>
        ),
      },
      {
        Header: "Actions",
        accessor: "action",
        Cell: ({ cell: { row } }) => (
          <span className="d-flex">
            <secondaryComponents.NavigateWithPermission
              permissionParentKey={"Inventory Management"}
              permissionChildKey="can_edit_product_category"
            >
              <primaryComponents.Button
                className="btn btn--primary smallBtn"
                onClick={() => {
                  setIsOpenEditDialog(true);
                  setSelectedCategory(row.original);
                }}
              >
                Edit
              </primaryComponents.Button>
            </secondaryComponents.NavigateWithPermission>
            <secondaryComponents.NavigateWithPermission
              permissionParentKey={"Inventory Management"}
              permissionChildKey="can_delete_product_category"
            >
              <primaryComponents.Button
                className="ml-2 btn btn--outline-red smallBtn"
                onClick={() => {
                  setIsOpenDeleteDialog(true);
                  setSelectedCategory(row.original);
                }}
              >
                Delete
              </primaryComponents.Button>
            </secondaryComponents.NavigateWithPermission>
          </span>
        ),
      },
    ],
    [],
  );

  const handleDeleteProductCategory = () => {
    if (selectedCategory === null) return;
    const { id } = selectedCategory;

    setIsDeletingCategory(true);
    setRequestLoaderProgress(30);

    deleteCategory(id)
      .then(() => {
        queryClient.invalidateQueries(["getProductCategoryList"]);
        setIsDeletingCategory(false);
        setRequestLoaderProgress(100);
        setIsOpenDeleteDialog(false);
      })
      .catch((error) => {
        setIsDeletingCategory(false);
        setRequestLoaderProgress(100);
      });
  };

  const instance = useTable({ columns, data: categories });

  return {
    instance,
    categories,
    selectedCategory,
    isOpenDeleteDialog,
    isOpenEditDialog,
    isDeletingCategory,
    setSelectedCategory,
    setIsOpenDeleteDialog,
    setIsOpenEditDialog,
    handleDeleteProductCategory,
  };
};

export default ProductCategoryTableLogic;
